import { CodeNameModel } from 'components/common/types';
import { TagCategory } from 'components/common/types/DirectSalesCampaign.types';
import {
  BusinessAreaResponse,
  FrameSizeFamilyResponse,
  ReferenceFormat,
  ReferenceFormatResponse,
  ReferenceLocation,
  ReferenceOptionsResponse,
  ReferenceTagResponse,
  TagGroup,
} from 'store/userOptions/reducer.types';
import { CodeName, LineLocation } from 'components/common/Deal/Location/Location.types';
import { UNDEFINED_RECORD_CODE } from './consts';

export const transformFormat = (formats: ReferenceFormatResponse): ReferenceFormat => {
  const initialFormat: ReferenceFormat = {
    ...formats,
    businessArea: [],
    revenueStream: [],
    frameSizeFamily: [],
    frameSize: [],
  };

  const formatsWithBusinessArea = formats.businessArea.reduce(
    (acc: ReferenceFormat, current: BusinessAreaResponse): ReferenceFormat => {
      const newRevenueStream = current.revenueStream.map((stream) => ({ ...stream, businessArea: current.code }));
      acc.revenueStream.push(...newRevenueStream);

      const { revenueStream, ...areaWithoutRevenue } = current;
      acc.businessArea.push(areaWithoutRevenue);

      return acc;
    },
    initialFormat,
  );

  return formats.frameSizeFamily.reduce((acc: ReferenceFormat, current: FrameSizeFamilyResponse): ReferenceFormat => {
    const newFrameSize = current.frameSize.map((size) => ({ ...size, frameSizeFamily: current.code }));
    acc.frameSize.push(...newFrameSize);

    const { frameSize, ...familyWithoutSize } = current;
    acc.frameSizeFamily.push(familyWithoutSize);

    return acc;
  }, formatsWithBusinessArea);
};

export const sortByCategory = (data: CodeNameModel[]): Record<string, CodeNameModel[]> => {
  return data
    .filter((value) => value.include)
    .reduce(
      (
        acc: Record<string, CodeNameModel[]>,
        { code, name, category }: { code: string; name: string; category: string },
      ) => {
        if (!acc[category]) {
          acc[category] = [{ code, name }];
        } else {
          acc[category].push({ code, name });
        }
        return acc;
      },
      {},
    );
};

export const sortByCategoryIncludeExclude = (
  data: CodeNameModel[],
): Record<string, { included?: CodeNameModel[]; excluded?: CodeNameModel[] }> => {
  return data.reduce(
    (
      acc: Record<string, { included?: CodeNameModel[]; excluded?: CodeNameModel[] }>,
      {
        code,
        name,
        nameWithMediaOwner,
        category,
        include,
      }: { code: string; name: string; category: string; include: boolean; nameWithMediaOwner?: string },
    ) => {
      const option = { code, name: nameWithMediaOwner ?? name };

      if (!acc[category]) {
        if (include) acc[category] = { included: [option] };
        else acc[category] = { excluded: [option] };
      } else if (include) {
        const { included } = acc[category];

        if (included) included.push(option);
        else acc[category].included = [option];
      } else {
        const { excluded } = acc[category];

        if (excluded) excluded.push(option);
        else acc[category].excluded = [option];
      }
      return acc;
    },
    {},
  );
};

export const transformTags = <T>(
  tagsResponse: T[],
  getTags: (curr: T) => { code: string; name: string }[],
  getKey: (curr: T) => string,
): Record<string, CodeNameModel[]> => {
  return tagsResponse.reduce((acc: Record<string, CodeNameModel[]>, curr) => {
    acc[getKey(curr)] = getTags(curr).map((subTag) => ({
      code: subTag.code,
      name: subTag.name,
      include: undefined,
    }));
    return acc;
  }, {});
};

export const transformProgrammaticTags = (tagsResponse: TagCategory[]): Record<string, CodeNameModel[]> => {
  return transformTags(
    tagsResponse,
    (curr) => curr.tags,
    (curr) => curr.name,
  );
};

export const transformDirectSalesTags = (tagsResponse: ReferenceTagResponse): Record<string, CodeNameModel[]> => {
  return transformTags(
    tagsResponse,
    (curr) => curr.tag,
    (curr) => curr.name,
  );
};

export const removeCategoryFromSelectedOptions = (selectedOptions: CodeNameModel[]): string[] => {
  return selectedOptions.filter((selectedOption) => selectedOption.include).map(({ name }) => name);
};

export const shouldDisplayCategoryClearAll = (allSelectedOptions: CodeNameModel[], optionCategory: string): boolean => {
  return allSelectedOptions.some((selectedOption) => selectedOption.category === optionCategory);
};

export const transformResellerAirportTags = (tagsResponse: TagGroup[][]): Record<string, CodeNameModel[]> => {
  return tagsResponse.flat().reduce(
    (acc, cur) => {
      if (cur.code === 'Airports_IATA') {
        acc.IATA.push(...cur.tag);
      }
      return acc;
    },
    { IATA: [] as { code: string; name: string; assets: number }[] },
  );
};

export const transformLocation = (
  referenceDataResponse: (Partial<ReferenceOptionsResponse> & { publisher?: string })[],
  locationCategories: (keyof ReferenceLocation)[],
  isReseller?: boolean,
): ReferenceLocation => {
  const allLocations = Object.fromEntries(locationCategories.map((key) => [key, []])) as Record<
    keyof ReferenceLocation,
    []
  >;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  return referenceDataResponse.reduce<ReferenceLocation>((locations, { location, publisher }) => {
    if (!location) return locations;

    const addLocationItem = (
      key: keyof ReferenceLocation,
      item: { code: string; name: string; assets: number },
      additionalItemData?: Partial<Record<keyof ReferenceLocation, string>>,
    ): void => {
      if (locations[key] && item.code !== UNDEFINED_RECORD_CODE) {
        const isResellerAndPublisher = isReseller && publisher;

        locations[key]!.push({
          assets: item.assets,
          code: item.code,
          name: item.name,
          ...(isResellerAndPublisher ? { nameWithMediaOwner: `${publisher}: ${item.name}` } : {}),
          ...(publisher && key !== 'address' ? { mediaOwner: publisher } : {}),
          ...additionalItemData,
        });
      }
    };

    if (locationCategories.includes('address')) {
      location.address?.forEach((address) => {
        addLocationItem('address', address);
      });
    }

    if (locationCategories.includes('postcode')) {
      location.postcode.forEach((postcode) => {
        addLocationItem('postcode', postcode);
      });
    }

    if (locationCategories.includes('conurbation')) {
      location.conurbation.forEach((conurbation) => {
        addLocationItem('conurbation', conurbation);
      });
    }

    if (locationCategories.includes('tvArea')) {
      location.tvArea.forEach((tvArea) => {
        addLocationItem('tvArea', tvArea);
      });
    }

    if (locationCategories.includes('routeTown')) {
      location.routeTown.forEach((routeTown) => {
        addLocationItem('routeTown', routeTown);
      });
    }

    location.country.forEach((country) => {
      if (locationCategories.includes('country')) {
        addLocationItem('country', country);
      }

      country.county.forEach((county) => {
        if (locationCategories.includes('county')) {
          addLocationItem('county', county, { country: country.code });
        }

        county.town.forEach((town) => {
          if (locationCategories.includes('town')) {
            addLocationItem('town', town, { county: county.code });
          }

          town.district.forEach((district) => {
            if (locationCategories.includes('district')) {
              addLocationItem('district', district, { town: town.code });
            }
          });
        });
      });
    });

    return locations;
  }, allLocations);
};

const CategoryToLineLocation: Record<string, keyof LineLocation> = {
  Country: 'countries',
  Street: 'streets',
  Town: 'cities',
  Postcode: 'postCodes',
  County: 'counties',
};

export const transformToProgrammaticLineLocation = (selectedLocations: CodeNameModel[]): LineLocation => {
  const lineLocation: LineLocation = {
    countries: [],
    streets: [],
    cities: [],
    postCodes: [],
    counties: [],
  };

  selectedLocations.forEach((location) => {
    if (location.category) {
      lineLocation[CategoryToLineLocation[location.category]].push(location);
    }
  });

  return lineLocation;
};

export const transformProgrammaticLineLocationToCodeNameModels = (
  lineLocation: LineLocation,
  isReseller?: boolean,
): CodeNameModel[] => {
  const { cities, counties, countries, postCodes, streets } = lineLocation;

  const sources: [string, CodeName[]][] = [
    ['Country', countries],
    ['Town', cities],
    ['Postcode', postCodes],
    ['Street', streets],
    ['County', counties],
  ];

  return sources.reduce<CodeNameModel[]>((acc, [category, items]) => {
    acc.push(
      ...(items ?? []).map(({ code, name, mediaOwner }) => {
        const isResellerAndMediaOwner = isReseller && mediaOwner;

        return {
          code,
          name,
          include: true,
          category,
          ...(mediaOwner ? { mediaOwner } : {}),
          ...(isResellerAndMediaOwner ? { nameWithMediaOwner: `${mediaOwner}: ${name}` } : {}),
        };
      }),
    );
    return acc;
  }, []);
};
